import React, { useState, useEffect } from 'react';
import { COUNTRY_DIAL_CODES, DEFAULT_COUNTRY_CODE } from '../../../constants';
import { splitPhoneNumber, formatNationalPhoneNumber } from '../../../utils';

/**
 * PhoneInputField
 * Uluslararası telefon girişi bileşeni.
 * - Varsayılan ülke kodu: +31 (Hollanda)
 * - Düzenlenebilir alan kodu (dropdown veya serbest kod girişi)
 * - Firebase'de tam numara olarak (örn: "31612345678" veya "905321234567") saklar.
 */
const PhoneInputField = ({
  value = '',
  onChange,
  required = false,
  disabled = false,
  placeholder = '6 XX XX XX XX',
  id
}) => {
  // Gelen value'dan dialCode ve nationalNumber çıkar
  const parsed = splitPhoneNumber(value, DEFAULT_COUNTRY_CODE);
  const [dialCode, setDialCode] = useState(parsed.dialCode || DEFAULT_COUNTRY_CODE);
  const [isCustom, setIsCustom] = useState(
    !COUNTRY_DIAL_CODES.some(c => c.code === (parsed.dialCode || DEFAULT_COUNTRY_CODE))
  );

  useEffect(() => {
    const nextParsed = splitPhoneNumber(value, DEFAULT_COUNTRY_CODE);
    if (nextParsed.dialCode) {
      setDialCode(nextParsed.dialCode);
      const isKnown = COUNTRY_DIAL_CODES.some(c => c.code === nextParsed.dialCode);
      setIsCustom(!isKnown);
    }
  }, [value]);

  const handleDialCodeSelect = (e) => {
    const selected = e.target.value;
    if (selected === 'custom') {
      setIsCustom(true);
      return;
    }
    setIsCustom(false);
    setDialCode(selected);

    // Mevcut numarayı yeni kodla birleştir
    const currentNational = parsed.nationalNumber;
    if (currentNational) {
      onChange(selected + currentNational);
    }
  };

  const handleCustomDialChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 4);
    setDialCode(raw);
    const currentNational = parsed.nationalNumber;
    if (currentNational) {
      onChange(raw + currentNational);
    }
  };

  const handleNumberChange = (e) => {
    const formatted = formatNationalPhoneNumber(e.target.value, dialCode);
    const digits = formatted.replace(/\D/g, '');
    const full = digits ? (dialCode || DEFAULT_COUNTRY_CODE) + digits : '';
    onChange(full);
  };

  const currentCountry = COUNTRY_DIAL_CODES.find(c => c.code === dialCode);
  const dynamicPlaceholder = placeholder || currentCountry?.format || 'Telefon numarası';

  return (
    <div className="phone-input-container">
      {!isCustom ? (
        <select
          className="phone-prefix-select"
          value={dialCode}
          onChange={handleDialCodeSelect}
          disabled={disabled}
          title="Ülke Alan Kodu"
        >
          {COUNTRY_DIAL_CODES.map(c => (
            <option key={c.code} value={c.code}>
              {c.flag} +{c.code}
            </option>
          ))}
          <option value="custom">+ Diğer</option>
        </select>
      ) : (
        <div className="phone-prefix-custom-wrap">
          <span className="phone-prefix-plus">+</span>
          <input
            type="text"
            className="phone-prefix-custom-input"
            value={dialCode}
            onChange={handleCustomDialChange}
            placeholder="Kod"
            maxLength={4}
            disabled={disabled}
            title="Özel Alan Kodu"
          />
          <button
            type="button"
            onClick={() => {
              setIsCustom(false);
              setDialCode(DEFAULT_COUNTRY_CODE);
              const currentNational = parsed.nationalNumber;
              if (currentNational) {
                onChange(DEFAULT_COUNTRY_CODE + currentNational);
              }
            }}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#94a3b8',
              fontSize: '11px',
              padding: '0 4px',
              display: 'flex',
              alignItems: 'center'
            }}
            title="Listeye dön"
          >
            ✕
          </button>
        </div>
      )}

      <input
        id={id}
        type="tel"
        className="phone-main-input"
        value={formatNationalPhoneNumber(parsed.nationalNumber, dialCode)}
        onChange={handleNumberChange}
        placeholder={dynamicPlaceholder}
        required={required}
        disabled={disabled}
      />
    </div>
  );
};

export default React.memo(PhoneInputField);
